import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../lib/auth';

/**
 * Gate for authenticated + onboarded routes.
 *   - Auth still hydrating → render a spacer; the redirect below would
 *     otherwise fire on cold page-load (when user is still null),
 *     bouncing signed-in users out to `/` and losing the target route.
 *     Manifested on 2026-08-24 as the wizard route racing to Readiness
 *     before the wizard could render.
 *   - No user               → redirect to /
 *   - User w/o profile      → redirect to /onboarding, UNLESS already on
 *     an onboarding route. `/w/:slug/onboarding` is wrapped in this gate
 *     so WorkspaceGate can hydrate the workspace; bouncing a new user off
 *     it to bare `/onboarding` strips the workspace context, and the
 *     wizard then saves the profile with `workspace_id = NULL` — which
 *     `GET /api/profiles/:id` refuses forever (assertProfileAccess),
 *     trapping the user in an onboarding loop (2026-09-15).
 *   - Otherwise             → render children
 */
const ONBOARDING_PATH = /^\/(?:w\/[^/]+\/)?onboarding(?:\/|$)/;

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) {
    return <div style={{ padding: '2rem' }} aria-busy="true" />;
  }
  if (!user) {
    // Remember a workspace deep link (e.g. returning from Microsoft consent) so sign-in lands back
    // there; AppRoot consumes this. Same-origin path only.
    if (loc.pathname.startsWith('/w/')) {
      try { window.sessionStorage.setItem('aigovops:post-signin-redirect', loc.pathname + loc.search); } catch { /* storage unavailable */ }
    }
    return <Navigate to="/" state={{ from: loc.pathname }} replace />;
  }
  if (!user.profile_id && !ONBOARDING_PATH.test(loc.pathname) && !/^\/w\/[^/]+\/app\/setup$/.test(loc.pathname)) {
    const workspaceSlug = /^\/w\/([^/]+)\//.exec(loc.pathname)?.[1] ?? user.default_workspace?.slug;
    return <Navigate to={workspaceSlug ? `/w/${workspaceSlug}/onboarding` : '/onboarding'} replace />;
  }
  return <>{children}</>;
}
